import React, { useState } from "react";
import ReactHtmlParser from "react-html-parser";

interface Props {
  selectedAnswer: any;
  question: any;
  answer: any;
  updateSelectedAnswer: (val: any) => void;
  submitAnswer: (e) => void;
  submitting: boolean;
  loadingAnswer: boolean;
  currentUser?: any;
  pollAnswerOptions: any;
  pollAnswerOptionsWithPercent: any;
}

const Poll: React.FC<Props> = ({
  question,
  answer,
  pollAnswerOptions,
  pollAnswerOptionsWithPercent,
  selectedAnswer,
  updateSelectedAnswer,
  currentUser,
  submitAnswer,
  submitting,
  loadingAnswer,
}) => {
  const [feedback, setFeedback] = useState("");

  const handleSubmit = (e) => {
    if (!selectedAnswer) {
      e.preventDefault();
      setFeedback("Choose an option before submitting.");
      return;
    }

    submitAnswer(e);
  };

  const renderAnswer = (opt, idx) => {
    const selected = opt[0]["id"] == answer.answer_option_id;

    return (
      // A div, not a disabled button. The result rows are not interactive, but
      // the option text carries links out to what was being voted on -- and a
      // disabled button suppresses pointer events from everything inside it,
      // so those links could not be clicked at all.
      <div
        className={`opt ${selected ? "selected-answer" : ""}`}
        key={idx}
      >
        <span className="fill" style={{ width: `${opt[1]}%` }} />
        <span
          dangerouslySetInnerHTML={{ __html: opt[0]["formatted_content"] }}
        />
        <span className="pc">{opt[1]}%</span>
      </div>
    );
  };

  const renderOption = (i, option, answer, selectedAnswer) => {
    const translation = option?.translations?.find(
      (t) => t.language === currentUser?.locale,
    );

    return (
      // A label round a radio, the same shape the multiple-choice options use,
      // rather than a button. An <a> is not valid inside a <button> and the
      // browser will not follow it; inside a label it behaves normally. Picking
      // an option is still one click anywhere on the row, and because the vote
      // is only cast by the Submit button below, catching the row while
      // reaching for a link costs nothing -- you can just pick again.
      <label
        className={`opt ${option.id == selectedAnswer ? "selected-answer" : ""}`}
        key={i}
      >
        <input
          className="opt__input"
          type="radio"
          name="poll-option"
          checked={option.id == selectedAnswer}
          onChange={() => {
            setFeedback("");
            updateSelectedAnswer(option.id);
          }}
        />
        <span className="fill" />
        <span
          dangerouslySetInnerHTML={{
            __html: translation?.formatted_content || option?.formatted_content,
          }}
        />
        <span className="pc" />
      </label>
    );
  };

  const translation = question?.translations?.find(
    (t) => t.language === currentUser?.locale,
  );

  return (
    <form className="q-card assignment-form">
      <div className="q-row">
        <span className="q-badge">Q</span>
        <div className="q-text">
          {ReactHtmlParser(
            translation
              ? translation?.formatted_description
              : question?.formatted_description,
          )}
        </div>
      </div>
      <div className="q-row q-answer">
        {(loadingAnswer || submitting) && (
          <div className="ui active inverted dimmer">
            <div className="ui text loader">Loading</div>
          </div>
        )}
        <span className="q-badge a">A</span>
        <div className="q-answer__body">
          <div className={`poll ${answer ? "voted" : ""}`}>
            {answer &&
              pollAnswerOptionsWithPercent.map((opt, idx) =>
                renderAnswer(opt, idx),
              )}
            {!answer &&
              pollAnswerOptions.map((option, i) =>
                renderOption(i, option, answer, selectedAnswer),
              )}
          </div>
          <p className="q-hint">
            {answer
              ? "Results are shown from everyone who has voted."
              : "Pick the one that fits. Results appear once you vote."}
          </p>
        </div>
      </div>
      {!answer && (
        <div className="q-actions">
          <span className={`q-feedback ${feedback ? "no" : ""}`}>
            {feedback}
          </span>
          <button
            className={`btn btn-blue btn--compact ${
              submitting ? "loading" : ""
            }`}
            type="submit"
            onClick={handleSubmit}
          >
            Submit
          </button>
        </div>
      )}
    </form>
  );
};

export default Poll;
